import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRotateRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  bulkImportService,
  EDITABLE_ROW_FIELDS,
  type EditableRowField,
  type BulkImportSessionStatus,
} from '@/services/bulk-import.service';
import { CLASSIFICATION_BADGE, CLASSIFICATION_LABELS } from './bulk-import-ui.constants';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { ROUTES } from '@/routes/routes';

const FIELD_LABELS: Record<EditableRowField, string> = {
  serialNumber: 'Serial Number',
  assetNumber: 'Asset Number',
  equipmentName: 'Equipment Name',
  manufacturer: 'Manufacturer',
  modelNumber: 'Model Number',
  equipmentType: 'Equipment Type',
  accessories: 'Accessories',
  purchaseDate: 'Purchase Date',
  notes: 'Notes',
};

function displayValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length > 0 ? v.join(', ') : '—';
  return String(v);
}

function rawFieldAsInputString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

interface BulkImportRowDetailModalProps {
  sessionId: string;
  sessionStatus: BulkImportSessionStatus;
  rowId: string | null;
  rowNumber?: number;
  onClose: () => void;
  onChanged: () => void;
}

function FieldCorrectionRow({
  field,
  raw,
  effective,
  canEdit,
  sessionId,
  rowId,
  onSaved,
}: {
  field: EditableRowField;
  raw: unknown;
  effective: unknown;
  canEdit: boolean;
  sessionId: string;
  rowId: string;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(rawFieldAsInputString(effective));
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      bulkImportService.correctRow(sessionId, rowId, {
        field,
        value: value.trim() === '' ? null : value.trim(),
      }),
    onSuccess: () => {
      setSaved(true);
      toast.success('Correction saved — revalidation required');
      onSaved();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to save correction')),
  });

  const changed = value.trim() !== rawFieldAsInputString(effective).trim();

  return (
    <div className="grid grid-cols-1 gap-2 border-b border-border py-3 last:border-0 sm:grid-cols-[repeat(4,1fr)] sm:items-center sm:gap-3">
      <div>
        <p className="text-xs font-medium text-foreground">{FIELD_LABELS[field]}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Original</p>
        <p className="text-sm text-foreground">{displayValue(raw)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Current (Effective)</p>
        <p className="text-sm text-foreground">{displayValue(effective)}</p>
      </div>
      {canEdit ? (
        <div className="flex items-center gap-2">
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setSaved(false);
            }}
            aria-label={`New value for ${FIELD_LABELS[field]}`}
            className="h-8 text-sm"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!changed || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
            ) : saved && !changed ? (
              <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
            ) : (
              'Save'
            )}
          </Button>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">Read-only</span>
      )}
    </div>
  );
}

export function BulkImportRowDetailModal({
  sessionId,
  sessionStatus,
  rowId,
  rowNumber,
  onClose,
  onChanged,
}: BulkImportRowDetailModalProps) {
  const queryClient = useQueryClient();

  const { data: row, isLoading } = useQuery({
    queryKey: ['bulk-import-row', sessionId, rowId],
    queryFn: () => bulkImportService.getRow(sessionId, rowId!),
    enabled: !!rowId,
  });

  const canCorrect = sessionStatus === 'REVIEW_REQUIRED' || sessionStatus === 'PARTIALLY_COMPLETED';

  const revalidateMutation = useMutation({
    mutationFn: () => bulkImportService.revalidate(sessionId, { rowIds: [rowId!] }),
    onSuccess: () => {
      toast.success('Row revalidated');
      void queryClient.invalidateQueries({ queryKey: ['bulk-import-row', sessionId, rowId] });
      onChanged();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to revalidate row')),
  });

  const handleFieldSaved = () => {
    void queryClient.invalidateQueries({ queryKey: ['bulk-import-row', sessionId, rowId] });
    onChanged();
  };

  const activeIssues = useMemo(
    () => (row ? row.issues.filter((i) => i.revalidationVersion === row.revalidationVersion) : []),
    [row],
  );
  const historicalIssues = useMemo(
    () => (row ? row.issues.filter((i) => i.revalidationVersion !== row.revalidationVersion) : []),
    [row],
  );

  return (
    <Modal
      open={!!rowId}
      onClose={onClose}
      title={rowNumber ? `Row ${rowNumber} — Detail` : 'Row Detail'}
      size="xl"
    >
      {isLoading || !row ? (
        <div className="flex justify-center py-10">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-6 w-6 animate-spin text-muted-foreground"
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CLASSIFICATION_BADGE[row.classification]}`}
            >
              {CLASSIFICATION_LABELS[row.classification]}
            </span>
            <span className="text-xs text-muted-foreground">
              Revalidation version {row.revalidationVersion}
            </span>
            {row.matchedEquipmentId && (
              <Link
                to={ROUTES.EQUIPMENT_DETAIL.replace(':id', row.matchedEquipmentId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                View matched Equipment record
              </Link>
            )}
          </div>

          {activeIssues.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Active Issues ({activeIssues.length})
              </p>
              <ul className="space-y-1.5">
                {activeIssues.map((issue) => (
                  <li
                    key={issue.id}
                    className={`rounded-lg border px-3 py-1.5 text-xs ${
                      issue.severity === 'ERROR'
                        ? 'border-red-500/30 bg-red-500/10 text-red-700'
                        : 'border-amber-500/30 bg-amber-500/10 text-amber-700'
                    }`}
                  >
                    <span className="font-medium">{issue.severity}</span>
                    {issue.field ? ` · ${issue.field}` : ''} — {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {historicalIssues.length > 0 && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">
                Historical issues ({historicalIssues.length}) — from earlier revalidation versions
              </summary>
              <ul className="mt-2 space-y-1.5">
                {historicalIssues.map((issue) => (
                  <li key={issue.id} className="rounded-lg border border-border px-3 py-1.5">
                    v{issue.revalidationVersion} · {issue.severity}
                    {issue.field ? ` · ${issue.field}` : ''} — {issue.message}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Fields {canCorrect ? '(editable)' : '(read-only — session is not in review)'}
            </p>
            <div>
              {EDITABLE_ROW_FIELDS.map((field) => (
                <FieldCorrectionRow
                  key={field}
                  field={field}
                  raw={row.rawValuesJson[field]}
                  effective={row.effectiveValuesJson[field]}
                  canEdit={canCorrect}
                  sessionId={sessionId}
                  rowId={row.id}
                  onSaved={handleFieldSaved}
                />
              ))}
            </div>
          </div>

          {row.corrections.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Correction History ({row.corrections.length})
              </p>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {row.corrections.map((c) => (
                  <li key={c.id} className="rounded-lg border border-border px-3 py-1.5">
                    <span className="font-medium text-foreground">
                      {FIELD_LABELS[c.field as EditableRowField] ?? c.field}
                    </span>
                    : {displayValue(c.previousValue)} → {displayValue(c.newValue)}{' '}
                    <span className="text-muted-foreground/70">
                      ({new Date(c.createdAt).toLocaleString()})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {row.proposedEquipmentJson && (
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Proposed New Equipment
              </p>
              <pre className="overflow-x-auto rounded-lg border border-border bg-muted/20 p-3 text-xs text-foreground">
                {JSON.stringify(row.proposedEquipmentJson, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex justify-between border-t border-border pt-4">
            <Button
              type="button"
              variant="secondary"
              disabled={!canCorrect || revalidateMutation.isPending}
              onClick={() => revalidateMutation.mutate()}
            >
              {revalidateMutation.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faRotateRight} className="mr-2 h-3 w-3" />
              )}
              Revalidate This Row
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
